import { ApolloProvider } from '@apollo/client';
import { queryClientOptions } from '@openx/constants/react-query.ts';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { Auth } from 'firebase/auth';
import type { KyInstance } from 'ky';
import { useSnackbar } from 'notistack';
import { type JSX, useCallback, useEffect, useMemo } from 'react';
import { configContext } from './configContext.ts';
import { extractOperationName } from './extractOperationName.ts';
import { createFederatedGraphQLRequest } from './federatedGraphQLRequest.ts';
import { createGraphQLRequest } from './graphQLRequest.ts';
import { makeApolloClient } from './makeApolloClient.ts';
import type { ConfigContextValue, GraphQLProviderProps, HandlersConfigMap } from './types.ts';

const messagesConfigMap: HandlersConfigMap = new Map();

export function GraphQLProvider({ auth, children }: GraphQLProviderProps & { auth: Auth }): JSX.Element {
  const { enqueueSnackbar } = useSnackbar();

  const configContextValue = useMemo<ConfigContextValue>(
    () => ({
      registerLocalErrorHandler: (document, handler) => {
        const operationName = extractOperationName(document);
        let handlers = messagesConfigMap.get(operationName) || [];
        handlers = handlers.concat(handler);
        messagesConfigMap.set(operationName, handlers);
      },
      unregisterLocalErrorHandler: (document, handler) => {
        const operationName = extractOperationName(document);
        let handlers = messagesConfigMap.get(operationName);

        if (!handlers) {
          // biome-ignore lint/suspicious/noConsole: logging error in console
          console.error(`No such local error handler registered. Handler: ${handler}`);
          return;
        }

        handlers = handlers.filter(h => h !== handler);

        if (handlers.length) {
          messagesConfigMap.set(operationName, handlers);
        } else {
          messagesConfigMap.delete(operationName);
        }
      },
    }),
    []
  );

  const printError = useCallback(
    message => {
      enqueueSnackbar(message, { variant: 'error' });
    },
    [enqueueSnackbar]
  );

  const apolloClient = useMemo(
    () => makeApolloClient({ auth, handlersConfigMap: messagesConfigMap, printError }),
    [printError, auth]
  );
  const federatedGraphQLRequest = useMemo(() => createFederatedGraphQLRequest({ auth }), [auth]);
  const graphQLRequest = useMemo(
    () => createGraphQLRequest({ auth, handlersConfigMap: messagesConfigMap, printError }),
    [auth, printError]
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: queryClient must be created once and stay stable for the provider's lifetime, or its cache would be discarded on every auth/printError change
  const queryClient = useMemo(
    // http client is not necessary in OA as we are using graphql
    () =>
      new QueryClient(
        queryClientOptions({ http: {} as KyInstance, meta: { graphQLRequest, federatedGraphQLRequest } })
      ),
    []
  );

  useEffect(() => {
    const { mutations, queries, ...defaultOptions } = queryClient.getDefaultOptions();

    queryClient.setDefaultOptions({
      ...defaultOptions,
      mutations: { ...mutations, meta: { ...mutations?.meta, graphQLRequest, federatedGraphQLRequest } },
      queries: { ...queries, meta: { ...queries?.meta, graphQLRequest, federatedGraphQLRequest } },
    });
  }, [queryClient, graphQLRequest, federatedGraphQLRequest]);

  return (
    <ApolloProvider client={apolloClient}>
      <QueryClientProvider client={queryClient}>
        <configContext.Provider value={configContextValue}>{children}</configContext.Provider>
      </QueryClientProvider>
    </ApolloProvider>
  );
}
